import { pipe, ReadonlyArray } from "@morphism/fp";
import * as D3 from "d3";
import React from "react";
import styled from "styled-components";

import { Mutable } from "~/Mutable";
import * as Typography from "~/Typography";

export function PieChart({
  title,
  data,
  height,
  width,
  innerRadius,
  outerRadius,
}: PieChart.Props) {
  const ref = React.useRef(null);

  const pieGenerator = D3.pie<PieChart.Datum>()
    .value((datum) => datum.count)
    .sort(null);

  const arcGenerator = D3.arc<d3.PieArcDatum<PieChart.Datum>>()
    .innerRadius(innerRadius)
    .outerRadius(outerRadius);

  React.useEffect(() => {
    const pie = pieGenerator(data as Mutable<typeof data>);
    const group = D3.select(ref.current);
    const groupWithData = group.selectAll("g.arc").data(pie);

    groupWithData.exit().remove();

    const groupWithUpdate = groupWithData
      .enter()
      .append("g")
      .attr("class", "arc");

    groupWithUpdate
      .append("path")
      .merge(groupWithData.select("path.arc"))
      .attr("class", "arc")
      .attr("d", arcGenerator)
      .attr("fill", ({ data: { color } }) => color);
  }, [data]);

  const legends = pipe(
    data,
    ReadonlyArray.map(({ name, count, percentage, color }) => (
      <PieChart.Legend key={name}>
        <div
          style={{
            height: 10,
            width: 25,
            backgroundColor: color,
            border: `1px solid ${color}`,
            borderRadius: 8,
          }}
        />
        <div style={{ marginLeft: 10, marginRight: 10 }}>{name}</div>
        <div style={{ marginLeft: "auto", fontWeight: 500 }}>
          {count} ({Math.round(percentage * 100)}%)
        </div>
      </PieChart.Legend>
    ))
  );

  return (
    <PieChart.Container>
      <Typography.Component strong>{title}</Typography.Component>
      <PieChart.Body>
        <svg width={width} height={height}>
          <g ref={ref} transform={`translate(${width / 2} ${height / 2})`} />
        </svg>
        <PieChart.Legends>{legends}</PieChart.Legends>
      </PieChart.Body>
    </PieChart.Container>
  );
}

export namespace PieChart {
  export type Props = {
    readonly title: string;
    readonly data: readonly Datum[];
    readonly width: number;
    readonly height: number;
    readonly innerRadius: number;
    readonly outerRadius: number;
  };

  export type Datum = {
    readonly name: string;
    readonly percentage: number;
    readonly count: number;
    readonly color: string;
  };

  export const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
  `;

  export const Body = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
  `;

  export const Legends = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin-left: 20px;
  `;

  export const Legend = styled.div`
    display: flex;
    flex-direction: row;
    align-items: baseline;
    justify-content: flex-start;
  `;
}
